import type { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from './AuthProvider.js';

export interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Auth guard (task spec: "unauthenticated → `/login`; session via cookie +
 * WS handshake"). `status === 'authenticating'` — a login/signup submit in
 * flight, OR `AuthProvider`'s boot-time silent-refresh check (E20 resume)
 * racing against this render on a hard reload — renders nothing rather than
 * bouncing to `/login` and immediately back once the token lands, avoiding a
 * redirect flash.
 */
export function RequireAuth({ children }: RequireAuthProps): ReactElement | null {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'authenticating') return null;

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
